import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../../shared/_TBComponents/Modal';
import Input from '../../../../../shared/_TBComponents/Input';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import ModalHeading from '../ModalHeading';
import TagModalShell, { Field, FieldLabel } from './TagModalShell';
import { showErrorToast } from '../../../../../shared/utils/toastManager';
import { createProgramTag, updateProgramTag } from '../../../../calendar/ducks/calendarActions';

// Create + edit modal for program tags (tag=null => create). Mirrors
// EditProgramModal's Field/Footer layout.

const PRESET_COLORS = [
  '#E74C3C', '#E67E22', '#F1C40F', '#2ECC71', '#1ABC9C',
  '#3498DB', '#9B59B6', '#34495E', '#95A5A6', '#FFFFFF',
];

const DEFAULT_COLOR = '#FFFFFF';

const SwatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SwatchButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) => props.color};
  border: 1px solid #D6D6D6;
  cursor: pointer;
  padding: 0;
  ${(props) => props.isSelected && 'box-shadow: 0 0 0 2px #1787FB;'}
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const ProgramTagModal = ({
  isOpen, onClose, accountCode, tag, onSaved,
}) => {
  const dispatch = useDispatch();
  const savedWorkouts = useSelector((state) => state.calendar.data.savedWorkouts);

  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [programIds, setProgramIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(tag?.name || '');
    setColor(tag?.color || DEFAULT_COLOR);
    setProgramIds((tag?.programs || []).map((p) => p.id));
  }, [isOpen, tag]);

  // Preserve a tag's existing color even if it isn't one of the presets.
  const swatchColors = useMemo(() => {
    if (color && !PRESET_COLORS.includes(color)) return [...PRESET_COLORS, color];
    return PRESET_COLORS;
  }, [color]);

  const programOptions = useMemo(
    () => [...(savedWorkouts || [])]
      .map((p) => ({ id: p.id, name: p.programName }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [savedWorkouts],
  );

  const selectedProgramOptions = programIds
    .map((id) => programOptions.find((o) => o.id === id))
    .filter(Boolean);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      showErrorToast('Tag name is required');
      return;
    }
    setIsSaving(true);
    const payload = { name: trimmed, color, saveInfoIds: programIds };
    const onDone = () => { setIsSaving(false); onSaved(); onClose(); };
    if (tag) {
      // updateProgramTag toasts its own errors.
      dispatch(updateProgramTag(accountCode, tag.id, payload, {
        onSuccess: onDone,
        onError: () => setIsSaving(false),
      }));
    } else {
      dispatch(createProgramTag(accountCode, payload, {
        onSuccess: onDone,
        onError: (message) => {
          setIsSaving(false);
          showErrorToast(message || 'Unable to create tag');
        },
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      customMaxWidth='480px'
      customMinHeight='0'
      customPadding='32px'
      customBorderRadius='8px'
      hideButton
    >
      <TagModalShell
        onCancel={onClose}
        onSave={handleSave}
        isSaving={isSaving}
        saveDisabled={isSaving}
      >
        <ModalHeading title={tag ? 'Edit Tag' : 'New Tag'} onClose={onClose} />
        <Field>
          <FieldLabel>Tag name</FieldLabel>
          <Input
            name='program-tag-name'
            inputOnly
            value={name}
            placeholder='Tag name'
            onChange={(e) => setName(e.target.value)}
            styledPropsInput={{ width: '100%', height: '40px', fontSize: '14px' }}
          />
        </Field>

        <Field>
          <FieldLabel>Color</FieldLabel>
          <SwatchRow>
            {swatchColors.map((c) => (
              <SwatchButton
                key={c}
                type='button'
                color={c}
                isSelected={color === c}
                aria-label={c}
                onClick={() => setColor(c)}
              />
            ))}
          </SwatchRow>
        </Field>

        <Field>
          <SelectBox
            title='Programs (optional)'
            titleStyledProps={{ fontSize: '14px', fontWeight: 700 }}
            options={programOptions}
            value={selectedProgramOptions}
            isMulti
            onChangeFunction={(selected) => setProgramIds((selected || []).map((o) => o.id))}
          />
        </Field>

      </TagModalShell>
    </Modal>
  );
};

ProgramTagModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  tag: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    color: PropTypes.string,
    programs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      programName: PropTypes.string,
    })),
  }),
  onSaved: PropTypes.func,
};

ProgramTagModal.defaultProps = {
  accountCode: undefined,
  tag: null,
  onSaved: () => {},
};

export default ProgramTagModal;
